import { Fragment, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LockClosedIcon, BuildingOffice2Icon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, PowerIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useAuth } from '../../lib/auth';
import { useSubscription } from '../../hooks/useSubscription';
import { useCapabilities } from '../../lib/capabilities';
import { useDiscovery } from '../../lib/discovery';
import { navRegistry, resolveEntries } from '../../lib/registries';
import type { ResolvedEntry } from '../../lib/registries';
import { moduleNavEntries } from '../../lib/host';
import { Cluster, IconButton, RailPanel, Stack, TokenBox, Tooltip } from '../ui';
import { AGENT_STATUS_EVENT, type AgentStatus } from '../../lib/agentEvents';
import type { Subscription } from '../../types';

interface SidebarProps {
  /** Mobile off-canvas open state — unchanged mechanism from before this story. */
  isOpen: boolean;
  onClose: () => void;
  /** Desktop/tablet collapsed (icons-only) vs expanded (icons+labels) state — owned by AppShell (localStorage-persisted), passed down so this stays a controlled, presentational component (P10 §1.1: no new React Context for shell state). */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

// Truthful per-tier plan-card copy (story 2-20). The card used to hard-code
// "TRIAL STATUS — Complimentary starter plan" for every persona and plan.
function planCardCopy(subscription: Subscription | null, orgKind: string | null): { title: string; body: string } {
  if (!subscription) {
    return { title: 'No active plan', body: 'This workspace has no subscription record yet. Set one up from Billing.' };
  }
  if (subscription.plan === 'trial' || subscription.status === 'trialing') {
    const ends = subscription.trialEndsAt ? new Date(subscription.trialEndsAt).toLocaleDateString() : null;
    return {
      title: 'Trial plan',
      body: ends ? `Your trial ends ${ends}. Upgrade to keep everything running.` : 'You are on a trial plan. Upgrade to keep everything running.',
    };
  }
  if (subscription.plan === 'free') {
    return {
      title: 'Free plan',
      body:
        orgKind === 'personal'
          ? 'Your personal workspace runs on the perpetual free plan. Upgrade anytime for more.'
          : 'Your organization runs on the perpetual free plan. Upgrade anytime for more.',
    };
  }
  const title = `${subscription.plan.charAt(0).toUpperCase()}${subscription.plan.slice(1)} plan`;
  if (subscription.status === 'past_due') return { title, body: 'Payment past due — update your billing details to keep access.' };
  if (subscription.status === 'canceled') return { title, body: 'Your subscription is canceled. Resume it or pick a plan from Billing.' };
  if (subscription.cancelAtPeriodEnd) return { title, body: 'Active — cancels at the end of the current billing period.' };
  return { title, body: 'Your subscription is active.' };
}

// Story 2-128 (UI-DS-00; 2026-08-23-ux-reference-pattern-mapping-design.md §2,
// §5) — context-panel GROUP for each nav item. `navRegistry`'s `section`
// field already covers the Administration group (the `access` row — its
// value stays the literal string 'Administration', asserted by name in
// `story-2-8.spec.mjs`'s `assertRetiredRowsGone`). Every OTHER row today
// carries no `section` at all (confirmed by reading every real
// RegistryEntry/module manifest before writing this map, per the mapping
// doc's own "confirm, don't reinvent" instruction — none of navRegistry's
// static entries or the reports/templates/billing/scheduling module
// manifests set `section` beside `access`). This lookup supplies the
// Focus/Library/Administration grouping the mapping doc's rail/context-panel
// row calls for WITHOUT writing to navRegistry or any module manifest — a
// module's contract stays "push a RegistryEntry with a key"; the SHELL (this
// file) decides how to bucket entries that don't already state a bucket.
// Verified: reports/index.ts, templates/index.ts, billing/index.ts,
// scheduling/index.ts are untouched by this story. A new nav key with no
// entry here simply renders without a group header (graceful, not a crash)
// until its own story opts it into a group — never an invented label.
const DEFAULT_SECTION_BY_KEY: Record<string, string> = {
  dashboard: 'Focus',
  'my-day': 'Focus',
  properties: 'Library',
  inspections: 'Library',
  reports: 'Library',
  templates: 'Library',
  activity: 'Administration',
  billing: 'Administration',
  settings: 'Administration',
};

function sectionOf(item: ResolvedEntry): string | undefined {
  return item.section ?? DEFAULT_SECTION_BY_KEY[item.key];
}

// Story 2-132 fix (named defect, this story's own done-when): the pre-2-132
// render loop computed `showSectionLabel = section !== sectionOf(previous
// item)` — ADJACENT-duplicate suppression, not grouping. Items render in
// registry order, so an interleaved order (e.g. a module contributing a
// Library-section row between two Focus-section rows) repeated a section
// label every time the run broke and resumed, observed live as
// FOCUS -> LIBRARY -> FOCUS -> LIBRARY. This groups items BY SECTION —
// each section's rows collect together, in the order the section was FIRST
// seen — so every label renders exactly once, with zero change to
// navRegistry/moduleNavEntries() or capability/flag gating (a pure
// rendering-order fix, operating only on the already-resolved item list).
function groupBySection(items: ResolvedEntry[]): { section: string | undefined; items: ResolvedEntry[] }[] {
  const groups: { section: string | undefined; items: ResolvedEntry[] }[] = [];
  const indexBySection = new Map<string | undefined, number>();
  for (const item of items) {
    const section = sectionOf(item);
    const idx = indexBySection.get(section);
    let group = idx === undefined ? undefined : groups[idx];
    if (!group) {
      group = { section, items: [] };
      indexBySection.set(section, groups.length);
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

export function Sidebar({ isOpen, onClose, collapsed, onToggleCollapsed }: SidebarProps): JSX.Element {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasPermission, featureState, doc } = useCapabilities();
  const { openTeaser } = useDiscovery();

  // Ambient agent illumination (story 2-132; docs/design/sitewise-ux-
  // reference.html's `.swa-rail` treatment) — the SAME AGENT_STATUS_EVENT
  // TopBar's own status pill already listens for (story 2-129), so the two
  // surfaces can never disagree; `data-agent-status` drives tokens.css's
  // existing `--agent-status` reassignment + this file's `.shell-agent-glow`
  // CSS, never a hardcoded color here.
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('idle');
  useEffect(() => {
    const onStatus = (event: Event) => {
      const detail = (event as CustomEvent<AgentStatus>).detail;
      if (detail) setAgentStatus(detail);
    };
    window.addEventListener(AGENT_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(AGENT_STATUS_EVENT, onStatus);
  }, []);

  // The nav is registry-driven (P16 §4, story 1C-1): entries come from
  // navRegistry and are filtered by the capability document (permission ∈ caps ∧
  // flag ≠ hidden). Owners hold every permission, so their nav is unchanged; a
  // feature adds a row to navRegistry, never touches this shell.
  const navItems = resolveEntries([...navRegistry, ...moduleNavEntries()], { hasPermission, featureState });
  const groups = groupBySection(navItems);

  // Plan card (story 2-20): only for personas who can act on billing — the same
  // permission that gates the Billing nav row and route, so the card's link
  // never dead-ends. Reads the REAL subscription (shared cache key with the
  // dashboard/pricing surfaces); 404 → null (no subscription record).
  const canManageBilling = hasPermission('org:billing:manage');
  const { data: subscription } = useSubscription(user?.organizationId, canManageBilling);
  const planCard = canManageBilling && subscription !== undefined ? planCardCopy(subscription, doc.orgKind) : null;

  const isPlatformActive = location.pathname === '/platform';

  const initials =
    user?.name
      ?.split(' ')
      .map((part) => part[0] ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase() ?? 'IN';

  const roleLabel =
    doc.platformRole === 'owner'
      ? 'Platform owner'
      : doc.platformRole === 'support'
        ? 'Platform support'
        : doc.roleNames.length > 0
          ? doc.roleNames.join(' · ')
          : 'Member';

  return (
    // Story 2-132 (P10 §1.1, P16 §4/§6.1): ONE navigation tree — a single
    // `<aside>` carrying a single `<nav>`, no parallel icon-rail-plus-
    // labeled-panel duo (2-127/2-128's model). Width is `--shell-sidebar-w`
    // (expanded) or `--shell-sidebar-collapsed-w` (collapsed) via the named
    // `w-sidebar`/`w-sidebar-collapsed` Tailwind utilities (tailwind.config.ts,
    // this story) — never a literal, so `check-shell-geometry.mjs` passes with
    // its Sidebar.tsx allowlist entry gone. `shell-agent-glow` +
    // `data-agent-status` (tokens.css, this story) render the ambient
    // illumination. `fixed bottom-0 left-0` + a `top` offset (below, own
    // comment) replaces the old `inset-y-0`; `-translate-x-full
    // sm:translate-x-0` is the SAME off-canvas mobile-drawer positioning
    // contract from before this story — unchanged mechanism, per the story's
    // own "keep this working, don't rebuild it" instruction.
    <TokenBox
      as="aside"
      data-agent-status={agentStatus}
      // `top: var(--shell-header-h)` (a token reference via inline style, not
      // a literal — check-shell-geometry.mjs's own carve-out, generalized
      // into the shared `TokenBox` primitive by story 2-147xd) instead of
      // `inset-y-0`: TopBar renders in normal flow ABOVE this fixed-positioned
      // element, so `inset-y-0`'s y=0 start made the sidebar's own opaque
      // header block visually cover TopBar's left side (found live — the
      // breadcrumb/title text was rendering behind the sidebar's org-identity
      // block). Starting below the header instead reads as one composed
      // surface with no overlap, at every breakpoint including the mobile
      // off-canvas drawer.
      top="var(--shell-header-h)"
      className={clsx(
        'shell-agent-glow fixed bottom-0 left-0 z-40 flex flex-col overflow-y-auto border-r border-border bg-card/95 shadow-sm transition-[transform,width] duration-300 ease-out',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        isOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'
      )}
    >
      {/* Header: organization identity + collapse toggle + mobile close. */}
      <Cluster gap={2} align="center" className={clsx('border-b border-border px-3 py-4', collapsed && 'flex-col')}>
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-sm font-bold text-primary"
          aria-hidden="true"
        >
          {(doc.orgName ?? user?.name ?? 'S').charAt(0).toUpperCase()}
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-caption uppercase tracking-eyebrow text-muted-foreground">
              {doc.orgKind === 'personal' ? 'Personal workspace' : 'Organization'}
            </p>
            <p className="truncate text-sm font-semibold">{doc.orgName ?? user?.name ?? 'Team'}</p>
          </div>
        )}
        {/* Two IconButtons, mutually exclusive by viewport (sm:flex / sm:hidden CSS,
            not simultaneous actions) — left as direct children rather than an
            ActionDescriptor host: ActionDescriptor's closed vocabulary (story
            2-147r) has no per-action responsive-visibility field, and adding one
            for this single call site would be a speculative, unjustified primitive
            growth (2-147xd honest-remainder call, same class of gap as the other
            named closed-vocabulary conflicts this story documents). */}
        <IconButton
          className="hidden shrink-0 text-muted-foreground transition hover:bg-muted hover:text-foreground sm:flex"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          pressed={collapsed}
          icon={collapsed ? <ChevronDoubleRightIcon className="h-4 w-4" /> : <ChevronDoubleLeftIcon className="h-4 w-4" />}
        />
        <IconButton
          className="shrink-0 text-muted-foreground sm:hidden"
          onClick={onClose}
          aria-label="Close navigation"
          icon={<span aria-hidden="true">✕</span>}
        />
      </Cluster>

      {/* Primary navigation — grouped by section (this story's fix: BY
          section, not adjacent-duplicate suppression, see groupBySection's
          own comment above). Collapsed state: icon only, aria-label as the
          accessible name, a visible Tooltip on hover/focus; section
          boundaries are a hairline separator instead of a repeated label
          (no room to render text at icon-only width).

          Deliberately FLAT (`nav`'s direct children are one wrapper per
          ITEM, via `groups.flatMap`, not one wrapper per group with items
          nested two levels deep): a group's label renders INSIDE its first
          item's own wrapper div, a leading sibling of that item's row —
          the exact shape the pre-2-132 single-tier render already had
          (`showSectionLabel` rendered inside the same per-item `<div>` as
          the link). `story-2-8.spec.mjs`'s own inventory reader
          (`document.querySelectorAll('aside nav > div')`, allowlisted in
          architecture-boundaries.allowlist.json as that story's own direct
          registry-content read) depends on exactly this — one row per item,
          each optionally carrying its own leading label — so this story's
          grouping fix stays a pure reordering with zero DOM-shape change to
          what that reader parses. */}
      {/* Collapsed: a centered icon column (the old icon-rail's own `flex
          flex-col items-center` — lost in this story's flat
          `groups.flatMap` render, found live as "icons scattered" against
          the rail's left edge instead of centered in its width). Expanded:
          the full-width labeled-row stack, unchanged. `Stack`'s own
          `align`-set branch already emits `flex flex-col ${align} space-y-*`
          (its established convention — space-y-* reproduces `gap-*` spacing
          identically for a non-wrapping flex column), so both branches are
          the same primitive with just `align` toggled, not two shapes. */}
      <Stack
        as="nav"
        gap={1}
        {...(collapsed ? { align: 'center' as const } : {})}
        className="flex-1 py-3 text-sm px-2"
        aria-label="Primary navigation"
      >
        {groups.flatMap((group, groupIndex) =>
          group.items.map((item, itemIndex) => {
            const Icon = item.icon;
            const active =
              item.to != null && (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
            // Documented exception, story 2-147xd — `layout-only-through-primitives`
            // (raw-flex): `rowClass` below is the flex+gap className of the row's
            // own INTERACTIVE element (`NavLink` when unlocked, `IconButton` when
            // locked, two DIFFERENT component types on the same ternary), not a
            // wrapping `<div>` — every Layout primitive's `as` prop only renders a
            // bare HTML tag (`keyof JSX.IntrinsicElements`), never a component.
            // Investigated widening `as` to `ElementType` (a generic polymorphic-
            // component pattern) and rejected it: (1) `Cluster` always forces
            // `flex-wrap`, a real behavioral change from this row's guaranteed
            // no-wrap for a long nav label; (2) `Inline` has no `justify` prop, and
            // the collapsed 44×44 icon box genuinely needs `justify-center` (a
            // single flex item does not center itself on the main axis); (3) most
            // importantly, `IconButton`'s `aria-label` is deliberately REQUIRED,
            // not optional — its own header comment states this closes the
            // accessible-name gap "by the component's required `aria-label`, not
            // by convention" — routing it through a primitive whose own
            // `aria-label` prop is optional would silently reopen exactly that
            // closed gap. Nesting a `Cluster`/`Inline` INSIDE the interactive
            // element instead would add a DOM level the row's own padding/
            // rounded-corner/hover classes are built around, changing the click
            // target's box. Same conclusion this file's own nearby org-identity/
            // primary-nav conversions already reached (see 2026-08-31 session
            // note) — left as a real, checked, structurally-blocked exception, not
            // an oversight.
            const rowClass = clsx(
              'focus-ring relative flex items-center rounded-xl font-medium transition',
              collapsed ? 'h-11 w-11 justify-center' : 'gap-3 px-3 py-2.5',
              active
                ? 'bg-[hsla(var(--primary)/0.15)] text-primary shadow-inner'
                : 'text-muted-foreground hover:bg-[hsla(var(--primary)/0.08)] hover:text-foreground'
            );
            const accessibleName = item.locked ? `${item.label} (locked)` : item.label;
            const showLabel = !collapsed && itemIndex === 0 && Boolean(group.section);
            const showSeparator = collapsed && itemIndex === 0 && groupIndex > 0;

            const row = item.locked ? (
              // Locked → open the discovery teaser (P02 §5, story 2-1) instead
              // of routing to a page that would 403. Emits a discovery impression.
              //
              // Story 2-147wa: real `IconButton` (P10 §4.4/G-22) instead of a
              // raw `<button>` — `IconButton`'s own `children` slot suppresses
              // its default icon-only box sizing whenever content is present
              // (story 2-147c), so `rowClass` (this row's own height/padding
              // contract, shared byte-for-byte with the real `NavLink` row
              // below) still fully controls this control's geometry, exactly
              // as before. `aria-label` is now ALWAYS a string (IconButton's
              // own required-name contract) rather than `undefined` when
              // expanded — set to `item.label` in that case, which is the
              // SAME accessible name the browser already computed from the
              // expanded row's own visible text before this change (measured:
              // the trailing `LockClosedIcon`'s own `aria-label` does not
              // contribute to a native `<button>`'s accessible name here,
              // confirmed by `story-2-10.spec.mjs`'s existing exact-match
              // `getByRole('button', { name: NAV_LABEL, exact: true })`
              // assertion, unchanged and still green after this migration).
              <IconButton
                aria-label={collapsed ? accessibleName : item.label}
                className={rowClass}
                icon={Icon ? <Icon className="h-5 w-5 shrink-0" /> : undefined}
                onClick={() => {
                  if (item.featureKey) openTeaser(item.featureKey, { name: item.label });
                  onClose();
                }}
              >
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    <LockClosedIcon className="h-4 w-4 text-muted-foreground" aria-label="Locked — upgrade to enable" />
                  </>
                )}
                {collapsed && (
                  <LockClosedIcon className="absolute bottom-1 right-1 h-3 w-3 text-muted-foreground" aria-hidden="true" />
                )}
              </IconButton>
            ) : (
              <NavLink
                to={item.to ?? '#'}
                aria-label={collapsed ? accessibleName : undefined}
                aria-current={active ? 'page' : undefined}
                className={rowClass}
                onClick={onClose}
              >
                {Icon && <Icon className="h-5 w-5 shrink-0" />}
                {!collapsed && <span className="flex-1">{item.label}</span>}
              </NavLink>
            );

            if (collapsed) {
              return (
                <Fragment key={item.key}>
                  {showSeparator && <hr aria-hidden="true" className="my-2 h-px w-8 shrink-0 border-0 bg-border" />}
                  <Tooltip label={item.label}>{row}</Tooltip>
                </Fragment>
              );
            }
            return (
              <div key={item.key} className="space-y-2">
                {showLabel && (
                  <p className="px-3 pt-3 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
                    {group.section}
                  </p>
                )}
                {row}
              </div>
            );
          })
        )}

        {/* Owner-plane console — gated on platformRole (not an org capability),
            same rule the rest of the nav follows. */}
        {user?.platformRole === 'owner' && (
          <div className="space-y-1">
            {collapsed && <hr aria-hidden="true" className="my-2 h-px w-8 shrink-0 border-0 bg-border" />}
            {!collapsed && (
              <p className="px-3 pt-3 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">Platform</p>
            )}
            {(() => {
              // Documented exception, story 2-147xd — same reasoning as the
              // primary-nav `rowClass` above (Layout primitives' `as` prop can't
              // render `NavLink`; `Cluster` would force `flex-wrap` and `Inline`
              // has no `justify` for the collapsed 44×44 box's required
              // `justify-center`). Left as a checked exception, not an oversight.
              const rowClass = clsx(
                'focus-ring flex items-center rounded-xl font-medium transition',
                collapsed ? 'h-11 w-11 justify-center' : 'gap-3 px-3 py-2.5',
                isPlatformActive
                  ? 'bg-[hsla(var(--primary)/0.15)] text-primary shadow-inner'
                  : 'text-muted-foreground hover:bg-[hsla(var(--primary)/0.08)] hover:text-foreground'
              );
              const row = (
                <NavLink
                  to="/platform"
                  aria-label={collapsed ? 'Platform console' : undefined}
                  aria-current={isPlatformActive ? 'page' : undefined}
                  className={rowClass}
                  onClick={onClose}
                >
                  <BuildingOffice2Icon className="h-5 w-5 shrink-0" />
                  {!collapsed && <span className="flex-1">Platform console</span>}
                </NavLink>
              );
              return collapsed ? <Tooltip label="Platform console">{row}</Tooltip> : row;
            })()}
          </div>
        )}
      </Stack>

      {/* Plan-usage card (story 2-20) — expanded state only; the collapsed
          rail has no room for card copy. Same logic/text/link as before this
          story. */}
      {!collapsed && planCard && (
        <div className="px-2 pb-2">
          <RailPanel title="Current plan">
            <p className="text-base font-semibold text-foreground">{planCard.title}</p>
            <p className="text-muted-foreground">{planCard.body}</p>
            <NavLink
              to="/app/billing"
              className="inline-flex items-center text-sm font-medium text-primary hover:underline"
              onClick={onClose}
            >
              Manage billing →
            </NavLink>
          </RailPanel>
        </div>
      )}

      {/* Account block (story 2-132) — moved out of TopBar entirely: avatar,
          identity and sign-out now live in the sidebar footer, not the
          header. Collapsed: avatar + a separate sign-out icon button, each
          with its own Tooltip; expanded: the original horizontal row. */}
      <Stack gap={2} align="center" className="border-t border-border p-3">
        {collapsed ? (
          <>
            <Tooltip label={`${user?.name ?? 'DefectSnap user'} — ${roleLabel}`}>
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary/60 to-accent/60 text-sm font-semibold text-white"
                aria-label={`${user?.name ?? 'DefectSnap user'} — ${roleLabel}`}
                role="img"
              >
                {initials}
              </div>
            </Tooltip>
            <Tooltip label="Sign out">
              <IconButton
                onClick={logout}
                shape="circle"
                className="border border-border text-muted-foreground transition hover:text-destructive"
                aria-label="Sign out"
                icon={<PowerIcon className="h-4 w-4" />}
              />
            </Tooltip>
          </>
        ) : (
          <Cluster gap={3} className="w-full">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/60 to-accent/60 text-sm font-semibold text-white"
              aria-hidden="true"
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-sm font-medium leading-none">{user?.name ?? 'DefectSnap user'}</p>
              <p className="truncate text-xs text-muted-foreground">{roleLabel}</p>
            </div>
            <IconButton
              onClick={logout}
              shape="circle"
              className="shrink-0 border border-border text-muted-foreground transition hover:text-destructive"
              aria-label="Sign out"
              icon={<PowerIcon className="h-4 w-4" />}
            />
          </Cluster>
        )}
      </Stack>
    </TokenBox>
  );
}
